import PropTypes from 'prop-types';
import moment from 'moment';

function isValidMoment(testMoment) {
  if (typeof moment.isMoment === 'function' && !moment.isMoment(testMoment)) {
    return false;
  }

  /* istanbul ignore else  */
  if (typeof testMoment.isValid === 'function') {
    // moment 1.7.0+
    return testMoment.isValid();
  }

  /* istanbul ignore next */
  return !isNaN(testMoment);
}

var momentValidationWrapper = {
  isValidMoment : isValidMoment,
};

var messages = {
  invalidPredicate: '`predicate` must be a function',
  invalidPropValidator: '`propValidator` must be a function',
  requiredCore: 'is marked as required',
  invalidTypeCore: 'Invalid input type',
  predicateFailureCore: 'Failed to succeed with predicate',
  anonymousMessage: '<<anonymous>>',
  baseInvalidMessage: 'Invalid ',
};

function constructPropValidatorVariations(propValidator) {
  if (typeof propValidator !== 'function') {
    throw new Error(messages.invalidPropValidator);
  }

  var requiredPropValidator = propValidator.bind(null, false, null);
  requiredPropValidator.isRequired = propValidator.bind(null, true, null);

  requiredPropValidator.withPredicate = function predicateApplication(predicate) {
    if (typeof predicate !== 'function') {
      throw new Error(messages.invalidPredicate);
    }
    var basePropValidator = propValidator.bind(null, false, predicate);
    basePropValidator.isRequired = propValidator.bind(null, true, predicate);
    return basePropValidator;
  };

  return requiredPropValidator;
}

function createInvalidRequiredErrorMessage(propName, componentName, value) {
  return new Error(
    'The prop `' + propName + '` ' + messages.requiredCore +
    ' in `' + componentName + '`, but its value is `' + value + '`.'
  );
}

var independentGuardianValue = -1;

function preValidationRequireCheck(isRequired, componentName, propFullName, propValue) {
  var isPropValueUndefined = typeof propValue === 'undefined';
  var isPropValueNull = propValue === null;

  if (isRequired) {
    if (isPropValueUndefined) {
      return createInvalidRequiredErrorMessage(propFullName, componentName, 'undefined');
    } else if (isPropValueNull) {
      return createInvalidRequiredErrorMessage(propFullName, componentName, 'null');
    }
  }

  if (isPropValueUndefined || isPropValueNull) {
    return null;
  }

  return independentGuardianValue;
}

function createMomentChecker(type, typeValidator, validator, momentType) {

  function propValidator(
    isRequired, // Bound parameter to indicate with the propType is required
    predicate, // Bound parameter to allow user to add dynamic validation
    props,
    propName,
    componentName,
    location,
    propFullName
  ) {
    var propValue = props[ propName ];
    var propType = typeof propValue;

    componentName = componentName || messages.anonymousMessage;
    propFullName = propFullName || propName;

    var preValidationRequireCheckValue = preValidationRequireCheck(
      isRequired, componentName, propFullName, propValue
    );

    if (preValidationRequireCheckValue !== independentGuardianValue) {
      return preValidationRequireCheckValue;
    }

    if (typeValidator && !typeValidator(propValue)) {
      return new Error(
        messages.invalidTypeCore + ': `' + propName + '` of type `' + propType + '` ' +
        'supplied to `' + componentName + '`, expected `' + type + '`.'
      );
    }

    if (!validator(propValue)) {
      return new Error(
        messages.baseInvalidMessage + location + ' `' + propName + '` of type `' + propType + '` ' +
        'supplied to `' + componentName + '`, expected `' + momentType + '`.'
      );
    }

    if (predicate && !predicate(propValue)) {
      var predicateName = predicate.name || messages.anonymousMessage;
      return new Error(
        messages.baseInvalidMessage + location + ' `' + propName + '` of type `' + propType + '` ' +
        'supplied to `' + componentName + '`. ' + messages.predicateFailureCore + ' `' +
        predicateName + '`.'
      );
    }

    return null;

  }

  return constructPropValidatorVariations(propValidator);

}

var core = {
  constructPropValidatorVariations: constructPropValidatorVariations,
  createMomentChecker: createMomentChecker,
  messages: messages,
};

var src = {

  momentObj : core.createMomentChecker(
    'object',
    function(obj) {
      return typeof obj === 'object';
    },
    function isValid(value) {
      return momentValidationWrapper.isValidMoment(value);
    },
    'Moment'
  ),

  momentString : core.createMomentChecker(
    'string',
    function(str) {
      return typeof str === 'string';
    },
    function isValid(value) {
      return momentValidationWrapper.isValidMoment(moment(value));
    },
    'Moment'
  ),

  momentDurationObj : core.createMomentChecker(
    'object',
    function(obj) {
      return typeof obj === 'object';
    },
    function isValid(value) {
      return moment.isDuration(value);
    },
    'Duration'
  ),

};

var intlShape = {
  formatMessage: PropTypes.func,
  formatDate: PropTypes.func,
  formatTime: PropTypes.func,
  formatNumber: PropTypes.func,
  formatPlural: PropTypes.func,
  formatRelativeTime: PropTypes.func,
  locale: PropTypes.string,
  messages: PropTypes.object,
  formats: PropTypes.object,
  defaultLocale: PropTypes.string,
  defaultFormats: PropTypes.object,
  onError: PropTypes.func
};
var trustYouProfileDataShape = {
  name: PropTypes.string.isRequired,
  score: PropTypes.string.isRequired,
  sources_count: PropTypes.number.isRequired,
  reviews_count: PropTypes.number.isRequired,
  ty_id: PropTypes.string.isRequired,
  score_description: PropTypes.string.isRequired
};
var trustYouProfileShape = {
  hasError: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  data: PropTypes.shape(trustYouProfileDataShape)
};
var trustYouReviewsDataShape = {
  content: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  stars: PropTypes.number.isRequired
};
var trustYouReviewsShape = {
  hasError: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape(trustYouReviewsDataShape))
};
var trustYouReducerShape = {
  profile: PropTypes.shape(trustYouProfileShape),
  reviews: PropTypes.shape(trustYouReviewsShape)
};
var paginationShape = {
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  pageSize: PropTypes.number,
  totalResults: PropTypes.number
};
var multiPropertyOfferShape = {
  code: PropTypes.string,
  price: PropTypes.number,
  originalPrice: PropTypes.number,
  discount: PropTypes.number
};
var currencyShape = {
  code: PropTypes.string.isRequired,
  symbol: PropTypes.string.isRequired
};
var formattedSearchParamsShape = {
  Checkin: PropTypes.string,
  Checkout: PropTypes.string,
  'Currency-code': PropTypes.string,
  'Language-code': PropTypes.string,
  'Total-rooms': PropTypes.string,
  'Widget-section': PropTypes.string,
  'Adult-room1': PropTypes.string
};
var domAttributesShape = {
  attribute: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
var feedbackMessagesShape = {
  noAvailableOffersMessage: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  notAvailablePropertiesTitleMessage: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  buttonMessage: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  reviewsCountMessage: PropTypes.func,
  reviewsModalTitleMessage: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  sortByLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.string])
};
var propertiesFiltersShape = {
  minPrice: PropTypes.number,
  maxPrice: PropTypes.number,
  stars: PropTypes.arrayOf(PropTypes.string),
  propertyName: PropTypes.string
};
PropTypes.oneOfType([PropTypes.number, PropTypes.string]);
var nodeOrStringProp = PropTypes.oneOfType([PropTypes.node, PropTypes.string]);
var nodeOrStringOrFuncProp = PropTypes.oneOfType([PropTypes.node, PropTypes.string, PropTypes.func]);
var mainImageShape = PropTypes.shape({
  title: PropTypes.string,
  url: PropTypes.string
});
var sortByMenuItemShape = PropTypes.shape({
  content: nodeOrStringProp.isRequired,
  isHighlighted: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  type: PropTypes.string,
  value: PropTypes.string.isRequired
});
var dateRangeShape = PropTypes.arrayOf(PropTypes.shape({
  state: PropTypes.string.isRequired,
  range: PropTypes.shape({
    start: PropTypes.object,
    end: PropTypes.object
  })
}));
var amenityShape = PropTypes.shape({
  icon: PropTypes.string,
  defaultIcon: PropTypes.string,
  name: PropTypes.string,
  text: PropTypes.string,
  total: PropTypes.number
});
var alternativeOffersShape = PropTypes.shape({
  checkIn: PropTypes.string,
  checkOut: PropTypes.string,
  los: PropTypes.number,
  price: PropTypes.number,
  priceWithExtraFees: PropTypes.number,
  shouldUpgrade: PropTypes.bool
});
var dayPricesShape = PropTypes.arrayOf(PropTypes.shape({
  day: src.momentObj,
  currencyCode: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  checkoutPrices: PropTypes.arrayOf(PropTypes.shape({
    day: src.momentObj,
    currencyCode: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }))
}));
var occupancyShape = {
  adults: PropTypes.number,
  children: PropTypes.arrayOf(PropTypes.number)
};
var specsShape = {
  maxOccupancy: PropTypes.number,
  size: PropTypes.number,
  sizeUnit: PropTypes.string,
  beds: PropTypes.array
};
var roomInfoShape = {
  code: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.shape(imageShape)),
  mainImage: PropTypes.shape(imageShape),
  amenities: PropTypes.arrayOf(amenityShape),
  specs: PropTypes.shape(specsShape),
  marketing: PropTypes.shape(roomInfoMarketingShape)
};
var roomInfoMarketingShape = {
  bestSeller: PropTypes.bool.isRequired,
  suite: PropTypes.bool.isRequired
};
var imageShape = {
  title: PropTypes.string,
  url: PropTypes.string,
  cropped: PropTypes.bool
};
({
  color: PropTypes.string,
  backgroundColor: PropTypes.string,
  content: PropTypes.element.isRequired
});
var featureCarouselsShape = {
  carousels: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    teaser: PropTypes.string,
    type: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      image: PropTypes.string,
      description: PropTypes.string
    }))
  })).isRequired,
  handleItemClick: PropTypes.func.isRequired,
  intl: PropTypes.shape(intlShape).isRequired
};

export { amenityShape as a, alternativeOffersShape as b, currencyShape as c, domAttributesShape as d, multiPropertyOfferShape as e, formattedSearchParamsShape as f, feedbackMessagesShape as g, trustYouReducerShape as h, intlShape as i, sortByMenuItemShape as j, propertiesFiltersShape as k, nodeOrStringOrFuncProp as l, mainImageShape as m, nodeOrStringProp as n, occupancyShape as o, paginationShape as p, dateRangeShape as q, roomInfoShape as r, src as s, trustYouProfileDataShape as t, dayPricesShape as u, trustYouReviewsShape as v, featureCarouselsShape as w };
//# sourceMappingURL=PropTypes-e1e509ba.js.map
